import { useEffect, type ReactNode } from 'react';
import { useSignageStore } from '@/store/signageStore';

/**
 * Stands in for `MqttProvider` when `VITE_PREVIEW_MODE=true`. See `@/lib/previewMode`.
 *
 * It deliberately does almost nothing: no preventer, no `savemeta`, no client. Widgets
 * still mount and still register their topics through `useWidgetData`, because that is what
 * the real app does and the preview should exercise the same render path — the topics
 * simply never get subscribed to.
 *
 * `moduleLoaded` and `preventerLoaded` are left alone. The gate they feed has no consumer
 * in this build, and faking `preventerLoaded` would put a fabricated token in the store
 * where a later reader could mistake it for a real one.
 */
export function PreviewModeProvider({ children }: { children?: ReactNode }) {
  useEffect(() => {
    // Read once, after the widget tree has mounted and registered, so the count is the real
    // one. Purely diagnostic: it is the quickest way to tell a config with genuinely static
    // widgets apart from one whose topics failed to register.
    const { topicItems, baseTopic } = useSignageStore.getState();
    console.info(
      '[preview] MQTT disabled. %d topic(s) registered under "%s" and none subscribed:\n  %s',
      topicItems.size,
      baseTopic,
      [...topicItems.keys()].join('\n  ') || '(none)',
    );
  }, []);

  return <>{children}</>;
}
